import { Component } from 'react'
import {
  Box,
  Button,
  Icons,
  Input,
  List,
  Page,
  ShippingRow,
  ShippingStartInput,
  ParcelListRow,
  Tab
} from 'stylewhere/components'
import { Router, ShippingOperationConfig, FormSchemaData, FormSchema } from 'stylewhere/shared'
import { showToastError } from 'stylewhere/utils'
import { Asn, Shippings, ShippingSelectData, ShippingParcel, ParcelContentEntry, Parcels } from 'stylewhere/api'
import { T, __ } from 'stylewhere/i18n'
import { Filters } from './Filters'

export interface Props {
  operation: ShippingOperationConfig
  onSelect: (data: ShippingSelectData, shipment: boolean) => void
  onCreate: (
    formData: FormSchemaData,
    operation: ShippingOperationConfig,
    schema: FormSchema,
    shipment: boolean
  ) => void
  onClickReadParcel: () => void
  shipment: boolean
  onDelete?: (parcel: ShippingSelectData, shipment: boolean) => void
  canDelete?: (parcel: ShippingSelectData, shipment: boolean) => boolean
  defaultFilter?: any
  onFiltersChange?: (filters) => void
}

interface Option {
  value: string
  label: string
  active: boolean
}

interface State {
  list?: ShippingSelectData[]
  search?: string
  loading: boolean
  shipment: boolean
  creating: boolean
  page: number
  totalPages: number
  totalElements: number
  loadingData: boolean
  selectValue: string | undefined
  filterParams: any
  options: Option[]
  resetFilters: number
  searchResetCounter: number
}

const PAGE_SIZE = 25

export class ShippingStartListAndInput extends Component<Props> {
  state: State = {
    list: undefined,
    search: this.props.defaultFilter?.search ?? '',
    loading: true,
    shipment: this.props.shipment,
    creating: false,
    page: 0,
    totalPages: 0,
    totalElements: 0,
    loadingData: false,
    selectValue: undefined,
    filterParams: { ...this.props.defaultFilter?.filters },
    resetFilters: 0,
    options: [
      {
        value: 'parcel',
        label: __(T.misc.parcels),
        active: !this.props.defaultFilter || this.props.defaultFilter?.tab === 'parcel'
      },
      { value: 'shipment', label: __(T.misc.shipment), active: this.props.defaultFilter?.tab === 'shipment' }
    ],
    searchResetCounter: 0
  }

  isModal = false
  operation: ShippingOperationConfig
  parcelCount: number = 0
  parcelCounterCodes: string[] = []
  loadingData = false

  constructor(props: Props) {
    super(props)
    this.operation = props.operation
  }

  componentDidMount() {
    this.loadData()
  }

  loadData = () => {
    this.setState({ loading: true }, this.load)
  }

  changeSearch = (search: string) => {
    const { onFiltersChange } = this.props
    const { filterParams, shipment } = this.state
    this.setState({ loading: true, search, page: 0, list: [] }, this.load)
    const currentTab = shipment ? 'shipment' : 'parcel'
    onFiltersChange?.(
      search ? { ...filterParams, search: search, tab: currentTab } : { ...filterParams, tab: currentTab }
    )
  }

  loadMore = () => {
    this.setState((prevState: State) => ({ loadingData: true, page: prevState.page + 1 }), this.load)
  }

  singleParcelCounter = async (parcel: ShippingParcel) => {
    const { operation } = this.props
    if (
      ['RECEIVED', 'READY_TO_RECEIVE', 'READY_TO_SEND', 'FORCE_RECEIVED', 'IN_TRANSIT', 'PACKED'].includes(
        parcel.status
      ) ||
      operation.persistParcelContentScannings === 'onBatchRead'
    ) {
      try {
        const entries: ParcelContentEntry[] = await Parcels.parcelContent(parcel.operationId, parcel.id, 'product')
        parcel.parcelEntryQuantity = 0
        parcel.parcelUnexpectedQuantity = 0
        entries.forEach((e) => {
          parcel.parcelEntryQuantity += e.detected
          parcel.parcelUnexpectedQuantity += e.unexpected
        })
      } catch (e) {
        console.log('error', e)
      }
    }
    return parcel
  }

  getParcelCounter = async (parcels: ShippingParcel[]) => {
    if (this.parcelCount < parcels.length) {
      if (!this.parcelCounterCodes.includes(parcels[this.parcelCount].code)) {
        this.parcelCounterCodes.push(parcels[this.parcelCount].code)
        const tmp = await this.singleParcelCounter(parcels[this.parcelCount])
        parcels[this.parcelCount] = tmp
      }
      this.parcelCount++
      return this.getParcelCounter(parcels)
    }
    return parcels
  }

  load = async () => {
    if (this.loadingData) return
    this.loadingData = true
    const { operation } = this.props
    const { page, search, list, filterParams, shipment } = this.state
    const cleanFilterParams = Object.entries(filterParams).reduce((acc, [key, value]) => {
      if (value === 'all') acc[key] = undefined
      else if (value !== undefined && value !== null && value !== '') acc[key] = value
      return acc
    }, {})
    try {
      const tmp = list ?? []
      const res = await Shippings.getShippingList(shipment, PAGE_SIZE, page, operation, search, cleanFilterParams)
      const result = res.content ?? []
      this.parcelCount = 0
      const data = shipment ? result : await this.getParcelCounter(result)
      this.setState({
        list: tmp.concat(data),
        totalPages: res.totalPages,
        totalElements: res.totalElements,
        loading: false,
        loadingData: false
      })
    } catch (error) {
      showToastError(error, __(T.error.error), this.isModal)
      this.setState({ list: [], loading: false, loadingData: false })
    } finally {
      this.loadingData = false
    }
  }

  onRowClick = (asn: ShippingSelectData) => this.props.onSelect(asn, this.state.shipment)

  viewAsnCounter = () => {
    const { operation } = this.props
    return operation.type === 'INBOUND' && operation.shippingMode !== 'shipment'
  }

  shipmentRender = (data: ShippingSelectData) => {
    const { shipment } = this.state
    const { operation, onDelete, canDelete } = this.props
    return shipment ? (
      <ShippingRow
        hideDate={operation.type === 'OUTBOUND'}
        shipping={data as Asn}
        onClick={() => this.onRowClick(data)}
        isInbound={operation.type === 'INBOUND'}
        operationConfig={operation}
        onDelete={onDelete}
        canDelete={canDelete ? canDelete(data as Asn, true) : false}
        viewCounter={this.viewAsnCounter()}
      />
    ) : (
      <ParcelListRow
        parcel={data as ShippingParcel}
        onClick={() => this.onRowClick(data)}
        isInbound={operation.type === 'INBOUND'}
        isPacking={operation.type === 'PACKING'}
        onDelete={onDelete}
        canDelete={canDelete ? canDelete(data as ShippingParcel, false) : false}
      />
    )
  }

  managerCreating = () => {
    this.setState((prevState: State) => ({ creating: !prevState.creating }), this.load)
  }

  addCreateButton = () => {
    const { shipment } = this.state
    const { operation } = this.props
    return (shipment && operation.canCreateShipments) || (!shipment && operation.canCreateParcels)
  }

  handleApplyFilters = (newFilters) => {
    this.setState({ filterParams: newFilters, page: 0, list: [], loading: true }, this.load)
    this.props.onFiltersChange?.(newFilters)
  }

  onTabClick = async (tab) => {
    this.parcelCount = 0
    this.parcelCounterCodes = []
    this.setState(
      (prevState: State) => ({
        shipment: tab === 'shipment',
        loading: true,
        page: 0,
        list: [],
        totalPages: 0,
        totalElements: 0,
        resetFilters: prevState.resetFilters + 1,
        searchResetCounter: prevState.searchResetCounter + 1,
        search: '',
        filterParams: {},
        options: prevState.options.map((opt) => ({
          ...opt,
          active: opt.value === tab
        }))
      }),
      this.load
    )
  }

  _onCreate = (formData: FormSchemaData, operation: ShippingOperationConfig, schema: FormSchema) => {
    const { onCreate } = this.props
    onCreate(formData, operation, schema, this.state.shipment)
  }

  render() {
    const {
      list,
      loading,
      creating,
      loadingData,
      totalElements,
      totalPages,
      page,
      options,
      shipment,
      filterParams,
      resetFilters,
      searchResetCounter,
      search
    } = this.state
    const { operation } = this.props
    const operationType = operation.type.toLowerCase()
    const withCreateButton = this.addCreateButton()
    const disabled = loading || loadingData
    return (
      <Page
        title={`${operation.description ?? operationType}`}
        onBackPress={() => {
          creating ? this.managerCreating() : Router.navigate('/')
        }}
      >
        {creating ? (
          <ShippingStartInput operation={this.operation} onCreate={this._onCreate} shipment={shipment} />
        ) : (
          <Page.Content notBoxed>
            <Box p={5} pb={0} mb={20} row>
              {operation.shippingMode === 'parcelByParcel' && operation.type !== 'PACKING' && (
                <Tab
                  options={options}
                  type="square"
                  style={{ marginRight: 10 }}
                  onOptionSelected={(opt) => {
                    this.onTabClick(opt)
                  }}
                  disabled={disabled}
                />
              )}
              <Input
                onChange={this.changeSearch}
                placeholder={`${shipment ? __(T.misc.shipment) : __(T.misc.parcel)}`}
                image={<Icons.Barcode />}
                style={{ width: 325 }}
                autoFocus
                debounce={500}
                resetCounter={searchResetCounter}
                defaultValue={search}
                disabled={disabled}
              />
              {withCreateButton && (
                <Button
                  title={`${shipment ? __(T.misc.new_shipment) : __(T.misc.new_parcel)}`}
                  onClick={this.managerCreating}
                  ml={16}
                  disabled={disabled}
                />
              )}
              <Box flex={1} justify="flex-end" row>
                <Filters
                  filterParams={filterParams}
                  onApplyFilters={this.handleApplyFilters}
                  resetFilters={resetFilters}
                  operation={operation}
                  disabled={disabled}
                />
              </Box>
            </Box>
            <Box p={5} flex={1} mt={0}>
              <List
                title={`${shipment ? __(T.titles.shipments) : __(T.titles.parcels)} (${totalElements})`}
                loading={loading}
                data={list}
                rowRender={this.shipmentRender}
                loadMore={this.loadMore}
                isNextPageLoading={loadingData}
                stopLoadMore={page === totalPages - 1}
              />
            </Box>
            {this.operation.startWithRfidSearch && (
              <Box p={5} pb={0} mt={40} row justify={'flex-end'}>
                <Button title={__(T.misc.read_parcel)} onClick={this.props.onClickReadParcel} />
              </Box>
            )}
          </Page.Content>
        )}
      </Page>
    )
  }
}
